import { useMemo, useState } from 'react'

import { objectHasNonNullableProp, objectMapValues } from '@app/utils/object'
import { isEmptyString, isEmptyArray, isNullable } from '@app/utils'

import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { dateFormat } from '@app/constants'

import { FILTERS_STATIC_KEYS } from '@app/constants/filters'

import { getFormattedDate } from '@app/utils/date'

import type { ChangeEvent } from 'react'
import type { Dayjs } from 'dayjs'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import type { LntRangeValue } from '@app/components/base/Form/Input/DatePicker/Range/types'
import type { ObjectValuesType } from '@app/types'
import type { FiltersParams } from '@app/types/filter'
import type { UseFiltersSearchParams, UseFiltersSearchReturn } from './types'

export const useFiltersSearch = ({ initialFilters, onSearch }: UseFiltersSearchParams): UseFiltersSearchReturn => {
  const [filters, setFilters] = useState(initialFilters)

  const canResetFilters = useMemo<boolean>(() => objectHasNonNullableProp(filters), [filters])

  const handleSearch = (): void => {
    onSearch(filters)
  }

  const handleClearFilters = (): void => {
    const clearedFilters = objectMapValues(filters, () => null)
    setFilters(clearedFilters)
  }

  const handleClearRelatedFilters = (relatedFilterKeys: Array<ObjectValuesType<typeof FILTERS_PARAMS_KEYS>>): void => {
    const clearedRelatedFilters = relatedFilterKeys.reduce((clearedRelatedFilters: FiltersParams, filterKey) => {
      clearedRelatedFilters[filterKey] = undefined

      return clearedRelatedFilters
    }, {})

    setFilters(prevFilters => ({ ...prevFilters, ...clearedRelatedFilters }))
  }

  const handleSelectFilterChange = <ValueType extends unknown[]>(
    key: ObjectValuesType<typeof FILTERS_PARAMS_KEYS>,
    value: ValueType,
    relatedFilterKeys: Array<ObjectValuesType<typeof FILTERS_PARAMS_KEYS>> = []
  ): void => {
    const updatedValue = isEmptyArray(value) ? undefined : value
    setFilters(prev => ({ ...prev, [key]: updatedValue }))

    if (isNullable(updatedValue) && !isEmptyArray(relatedFilterKeys)) {
      handleClearRelatedFilters(relatedFilterKeys)
    }
  }

  const handleInputFilterChange = (
    key: ObjectValuesType<typeof FILTERS_PARAMS_KEYS>,
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value
    const validValue = isEmptyString(value) ? undefined : value
    setFilters(prev => ({ ...prev, [key]: validValue }))
  }

  const handleCreatedRangeDateChange = (
    dateRangeValue: LntRangeValue,
    dateRangeFilterKeysTuple: [string, string],
    format = dateFormat.YYYY_MM_DD
  ): void => {
    const [afterDateFilterKey, beforeDateFilterKey] = dateRangeFilterKeysTuple ?? [null, null]
    const [afterValue, beforeValue] = dateRangeValue ?? [null, null]

    setFilters(prev => ({
      ...prev,
      [afterDateFilterKey]: getFormattedDate(afterValue, format),
      [beforeDateFilterKey]: getFormattedDate(beforeValue, format)
    }))
  }

  const handleCheckboxChange = (filterKey: string, value: ObjectValuesType<typeof FILTERS_STATIC_KEYS> | boolean) => {
    return (event: CheckboxChangeEvent) => {
      if (event.target.checked) {
        setFilters(prev => ({ ...prev, [filterKey]: value }))
      } else {
        setFilters(prev => ({ ...prev, [filterKey]: undefined }))
      }
    }
  }

  const handleWeekChange = (
    value: Dayjs | null,
    afterKey: string = FILTERS_PARAMS_KEYS.createdAtAfter,
    beforeKey: string = FILTERS_PARAMS_KEYS.createdAtBefore
  ): void => {
    setFilters(prev => ({
      ...prev,
      [afterKey]: null,
      [beforeKey]: null,
      week: getFormattedDate(value, dateFormat.W)
    }))
  }

  return {
    filters,
    canResetFilters,
    setFilters,
    handleSearch,
    handleClearFilters,
    handleCheckboxChange,
    handleInputFilterChange,
    handleSelectFilterChange,
    handleCreatedRangeDateChange,
    handleWeekChange
  }
}
